/**
 * Linked Products Drawer Component
 *
 * Displays product siblings in a grid layout for PDP color selection drawer.
 * Reuses the fetch logic from component-linked.js but renders as a grid with images.
 *
 * Usage:
 * <div data-component="linked-drawer" data-title="Product Name - Color"></div>
 */

class LinkedProductsDrawerComponent {
  constructor(element) {
    this.element = element;
    this.productTitle = element.dataset.title || '';
    this.products = [];

    this.config = {
      titleSeparator: '-',
      apiEndpoint: '/collections/all',
    };
  }

  async init() {
    try {
      // Reuse the fetch logic from LinkedProductsComponent if available
      if (window.LinkedProductsComponent) {
        const linkedComponent = new window.LinkedProductsComponent(document.createElement('div'));
        this.products = await linkedComponent.fetchProducts();
      } else {
        console.error('[LinkedProductsDrawer] LinkedProductsComponent not found. Make sure component-linked.js is loaded first.');
        return;
      }

      const siblings = this.findSiblings();

      console.log('[LinkedProductsDrawer] Current product:', this.productTitle);
      console.log('[LinkedProductsDrawer] Found siblings:', siblings.map(s => s.title));

      if (siblings.length > 0) {
        this.render(siblings);
      }
    } catch (error) {
      console.error('[LinkedProductsDrawer] Failed to initialize', error);
    }
  }

  /**
   * Find sibling products by matching base title
   */
  findSiblings() {
    const baseTitle = this.extractBaseTitle(this.productTitle);

    const siblings = this.products.filter(product => {
      const productBaseTitle = this.extractBaseTitle(product.title);
      return productBaseTitle.toLowerCase() === baseTitle.toLowerCase();
    });

    // Sort alphabetically by color name
    siblings.sort((a, b) => {
      const colorA = this.extractColor(a);
      const colorB = this.extractColor(b);
      return colorA.localeCompare(colorB);
    });

    return siblings;
  }

  /**
   * Extract base title by removing color suffix
   * "T-Shirt - Black" -> "T-Shirt"
   */
  extractBaseTitle(title) {
    const parts = title.split(this.config.titleSeparator);

    if (parts.length > 1) {
      return parts.slice(0, -1).join(this.config.titleSeparator).trim();
    }

    return title.trim();
  }

  /**
   * Extract color from product title
   */
  extractColor(product) {
    const parts = product.title.split(this.config.titleSeparator);
    if (parts.length > 1) {
      return parts[parts.length - 1].trim();
    }
    return '';
  }

  /**
   * Escape HTML to prevent XSS attacks
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Get optimized image URL for drawer display
   */
  getOptimizedImageUrl(imageUrl, width = 800) {
    if (!imageUrl) return '';

    // Remove any existing size parameters
    const baseUrl = imageUrl.split('?')[0];

    // Add Shopify image size parameter
    return `${baseUrl}?width=${width}`;
  }

  /**
   * Render product grid
   */
  render(siblings) {
    const gridHtml = siblings.map(sibling => {
      const color = this.extractColor(sibling);
      // Compare titles case-insensitively and trim whitespace
      const isActive = sibling.title.trim().toLowerCase() === this.productTitle.trim().toLowerCase();
      const featuredImage = sibling.featured_image || '';
      const optimizedImage = this.getOptimizedImageUrl(featuredImage, 800);

      // Escape all user-controlled data
      const escapedUrl = this.escapeHtml(sibling.url || '');
      const escapedColor = this.escapeHtml(color);
      const escapedAlt = this.escapeHtml(sibling.title || '');

      return `
        <div class="linked-drawer-item ${isActive ? 'linked-drawer-item--active' : ''}">
          <a href="${escapedUrl}" class="linked-drawer-item__link">
            <div class="linked-drawer-item__image">
              ${optimizedImage ? `
                <img
                  src="${optimizedImage}"
                  alt="${escapedAlt}"
                  loading="lazy"
                  class="linked-drawer-item__img"
                  srcset="${this.getOptimizedImageUrl(featuredImage, 400)} 400w,
                          ${this.getOptimizedImageUrl(featuredImage, 600)} 600w,
                          ${this.getOptimizedImageUrl(featuredImage, 800)} 800w"
                  sizes="(min-width: 769px) 33vw, 50vw"
                >
              ` : `
                <div class="linked-drawer-item__placeholder"></div>
              `}
            </div>
            <div class="linked-drawer-item__color ds-label-haltimezone">
              ${escapedColor}
            </div>
          </a>
        </div>
      `;
    }).join('');

    this.element.innerHTML = `<div class="linked-drawer-grid">${gridHtml}</div>`;
  }
}

/**
 * Initialize all linked drawer components on the page
 */
async function initLinkedDrawers() {
  const elements = document.querySelectorAll('[data-component="linked-drawer"]');

  if (elements.length === 0) return;

  // Initialize all components
  const components = Array.from(elements).map(el => new LinkedProductsDrawerComponent(el));

  // Wait for all to initialize
  await Promise.all(components.map(c => c.init()));
}

// Auto-initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLinkedDrawers);
} else {
  initLinkedDrawers();
}

// Export for manual initialization if needed
window.LinkedProductsDrawerComponent = LinkedProductsDrawerComponent;
window.initLinkedDrawers = initLinkedDrawers;
